import { API_URL } from '@/config/api';

class ApiClient {
  private token: string | null = null;

  getToken(): string | null {
    if (!this.token) {
      this.token = localStorage.getItem('token');
    }
    return this.token;
  }

  setToken(token: string) {
    this.token = token;
    localStorage.setItem('token', token);
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }

  private async request(endpoint: string, options?: RequestInit): Promise<Response> {
    const url = `${API_URL}${endpoint}`;
    const token = this.getToken();
    
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options?.headers,
    };

    if (token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (response.status === 401) {
      // Only redirect if we actually have a token (prevent loops on public pages)
      if (this.getToken()) {
        this.clearToken();
        // Use setTimeout to avoid redirect during render cycle
        setTimeout(() => { window.location.href = '/login'; }, 100);
      }
    }

    return response;
  }

  async get(endpoint: string): Promise<Response> {
    return this.request(endpoint, { method: 'GET' });
  }

  async post(endpoint: string, data?: unknown): Promise<Response> {
    return this.request(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch(endpoint: string, data?: unknown): Promise<Response> {
    return this.request(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put(endpoint: string, data?: unknown): Promise<Response> {
    return this.request(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete(endpoint: string): Promise<Response> {
    return this.request(endpoint, { method: 'DELETE' });
  }
}

export const apiClient = new ApiClient();

export const api = {
  get: (endpoint: string) => apiClient.get(endpoint),
  post: (endpoint: string, data?: unknown) => apiClient.post(endpoint, data),
  patch: (endpoint: string, data?: unknown) => apiClient.patch(endpoint, data),
  put: (endpoint: string, data?: unknown) => apiClient.put(endpoint, data),
  delete: (endpoint: string) => apiClient.delete(endpoint),
};
